import { NextRouter, useRouter } from 'next/router'
import { useIntl } from 'react-intl'

import { SiderMenuItem } from './SiderMenuItem'
import { MenuItem } from '@/components/ui/common/MenuItem'
import { AnalyticsFilled } from '@/components/ui/icons'
import urls from '@/config/uiUrls'
import { buildI18nFunction, IntlFunction } from '@/utils/messages'
import { isWorkspaceUserAdmin } from '@/utils/workspaces'

import {
  Workspace,
  WorkspaceFeaturesForm,
  WorkspaceUser,
} from '@/types/index.d'

type AnalyticsSubMenuProps = {
  membership: WorkspaceUser
  showPaywallModal: (id: string) => void | null
  workspace: Workspace
}

export type AnalyticsItemsType = {
  name: string
  featureName: keyof Omit<WorkspaceFeaturesForm, 'custom_rule'>
  url: string
  isNewFeature?: boolean
  validateMembership: (membership: WorkspaceUser) => boolean
}

const analyticsItems: AnalyticsItemsType[] = [
  {
    name: 'hardening',
    featureName: 'analytics',
    url: urls.analytics.getHardening,
    validateMembership: () => true,
  },
  {
    name: 'impact',
    url: urls.analytics.getImpact,
    featureName: 'impact_report',
    validateMembership: isWorkspaceUserAdmin,
  },
  {
    name: 'security',
    url: urls.analytics.getSecurity,
    featureName: 'security_insights',
    validateMembership: isWorkspaceUserAdmin,
  },
  {
    name: 'people',
    url: urls.analytics.getPeople,
    featureName: 'people_insights',
    validateMembership: isWorkspaceUserAdmin,
  },
]

export function AnalyticsSubMenu({
  membership,
  showPaywallModal,
  workspace,
}: AnalyticsSubMenuProps): any {
  const t = buildI18nFunction(useIntl().formatMessage)
  const router = useRouter()

  return SiderMenuItem({
    feature: 'analytics',
    href: undefined,
    key: 'insights',
    router,
    showPaywallModal,
    t,
    workspace,
    icon: <AnalyticsFilled />,
    children: getSubMenuItems(
      membership,
      showPaywallModal,
      workspace,
      t,
      router
    ),
  })
}

function getSubMenuItems(
  membership: WorkspaceUser,
  showPaywallModal: (id: string) => void,
  workspace: Workspace,
  t: IntlFunction,
  router: NextRouter
): MenuItem[] {
  return [
    ...analyticsItems.map(
      ({
        name,
        url,
        validateMembership,
        featureName,
        ...rest
      }: AnalyticsItemsType) =>
        validateMembership(membership) &&
        SiderMenuItem({
          feature: featureName,
          href: url,
          key: `insights-${name}`,
          showPaywallModal,
          t,
          workspace,
          router,
          ...rest,
        })
    ),
  ]
}
